import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";

import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { environment } from "../../../../environments/environment";
import { TaskCategory, TaskCategoryScope } from "../domains/task-category";

export interface CreateCategoryPayload {
  name: string;
  color: string;
  icon?: string;
  scope?: TaskCategoryScope;
}

@Injectable({
  providedIn: "root",
})
export class TaskCategoryService {
  url = environment.apiUrl + "/task-categories";

  private categoriesSubject = new BehaviorSubject<TaskCategory[]>([]);
  public categories$ = this.categoriesSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Retourne les categories visibles pour l'utilisateur :
   * - Categories company de l'entreprise
   * - Categories personnelles de l'utilisateur connecte
   */
  getCategories(): Observable<TaskCategory[]> {
    return this.http.get<any[]>(this.url).pipe(
      map(TaskCategory.createInstances),
      tap((categories) => this.categoriesSubject.next(categories)),
    );
  }

  /**
   * Cree une nouvelle categorie.
   * @param category Payload avec name, color, et optionnellement scope
   */
  createCategory(category: CreateCategoryPayload): Observable<TaskCategory> {
    return this.http.post<any>(this.url, category).pipe(
      map(TaskCategory.createInstance),
      tap((created) => {
        // Ajouter la nouvelle categorie au cache local
        const current = this.categoriesSubject.getValue();
        this.categoriesSubject.next([...current, created]);
      }),
    );
  }

  updateCategory(categoryId: string, category: any): Observable<TaskCategory> {
    return this.http.put<any>(`${this.url}/${categoryId}`, category).pipe(
      map(TaskCategory.createInstance),
      tap((updated) => {
        // Mettre a jour le cache local
        const current = this.categoriesSubject.getValue();
        const index = current.findIndex((c) => c.category_id === categoryId);
        if (index >= 0) {
          current[index] = updated;
          this.categoriesSubject.next([...current]);
        }
      }),
    );
  }

  /**
   * Supprime une categorie (soft-delete).
   * La categorie reste visible sur les taches existantes mais n'apparait plus dans la selection.
   */
  deleteCategory(categoryId: string): Observable<any> {
    return this.http.delete<any>(`${this.url}/${categoryId}`).pipe(
      tap(() => {
        // Marquer comme deleted dans le cache local
        const current = this.categoriesSubject.getValue();
        const updated = current.map((c) =>
          c.category_id === categoryId ? ({ ...c, is_deleted: true } as TaskCategory) : c,
        );
        this.categoriesSubject.next(updated);
      }),
    );
  }

  reorderCategories(categoryIds: string[]): Observable<TaskCategory[]> {
    return this.http.put<any[]>(`${this.url}/reorder`, { category_ids: categoryIds }).pipe(
      map(TaskCategory.createInstances),
      tap((categories) => this.categoriesSubject.next(categories)),
    );
  }

  /**
   * Retourne les categories visibles (non supprimees).
   */
  getVisibleCategories(): TaskCategory[] {
    return this.categoriesSubject.getValue().filter((c) => !c.is_deleted);
  }

  /**
   * Retourne une categorie par son ID (y compris supprimees pour affichage).
   */
  getCategoryById(categoryId: string): TaskCategory | undefined {
    return this.categoriesSubject.getValue().find((c) => c.category_id === categoryId);
  }

  /**
   * Retourne plusieurs categories par leurs IDs.
   */
  getCategoriesByIds(categoryIds: string[]): TaskCategory[] {
    const all = this.categoriesSubject.getValue();
    return categoryIds.map((id) => all.find((c) => c.category_id === id)).filter((c): c is TaskCategory => !!c);
  }
}
